import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useLocalNotifications } from "@/hooks/useLocalNotifications";

export function usePreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const notifications = useLocalNotifications();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["preferences", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<typeof preferences>) => {
      if (!user) throw new Error("No user");
      // CRITICAL: onConflict: "user_id" is required. user_preferences
      // has UNIQUE(user_id) and a row is auto-created on signup by the
      // handle_new_user trigger. Without onConflict, supabase-js
      // defaults to the PK (`id`) for conflict resolution — we don't
      // pass an id, so it attempts a fresh INSERT, hits the UNIQUE
      // constraint and fails with 23505. That bug silently dropped
      // every preferences update (onboarding_completed never landed,
      // training_type / days_per_week were never persisted), so the
      // onboarding flow appeared broken — it re-fired on other devices
      // and the personalisation never took effect.
      const { data, error } = await supabase
        .from("user_preferences")
        .upsert(
          { user_id: user.id, ...updates },
          { onConflict: "user_id" },
        )
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["preferences", user?.id] });
      // If the user just toggled workout_reminders OFF, cancel any
      // pending local notifications so they don't keep firing. We do
      // this in onSuccess (not in mutationFn) so a failed DB write
      // doesn't wipe scheduled reminders.
      if (variables && Object.prototype.hasOwnProperty.call(variables, "workout_reminders")
          && (variables as { workout_reminders?: boolean }).workout_reminders === false) {
        notifications.cancelAll().catch(() => {});
      }
    },
  });

  return { preferences, isLoading, updatePreferences };
}
